import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import MetricCard from '../../dashboard/components/MetricCard';
import UsageMetric from '../../dashboard/components/UsageMetric';
import RecentActivityTable from '../../dashboard/components/RecentActivityTable';
import AlertsTable from '../../alerts/components/AlertsTable';
import AlertsSearchFilters from '../../alerts/components/AlertsSearchFilters';
import CandidatesTable from '../../candidates-list/components/CandidatesTable';
import SearchAndFilters from '../../candidates-list/components/SearchAndFilters';
import AddCandidateButton from '../../candidates-list/components/AddCandidateButton';
import FileUploadZone from '../../import-candidates/components/FileUploadZone';
import UploadInstructions from '../../import-candidates/components/UploadInstructions';
import AICallsTable from '../../ai-assistant/components/AICallsTable';
import {
  CALL_ACTIVITY_PERIODS,
  CALL_ACTIVITY_PERIOD_OPTIONS,
} from '../../../lib/utils/callActivityPeriod';
import {
  getDemoActivities,
  getDemoCalls,
  getDemoCandidates,
} from '../../../lib/demo/demoMockData';

const noop = () => {};
const PREVIEW_SCALE = 0.68;

const PreviewFrame = ({ children }) => {
  const { t } = useTranslation();
  const innerRef = useRef(null);
  const [frameHeight, setFrameHeight] = useState(0);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;

    const updateHeight = () => {
      setFrameHeight(Math.ceil(el.offsetHeight * PREVIEW_SCALE));
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [children]);

  return (
    <div className="rounded-xl border border-border bg-card p-2 md:p-3 mb-4 shadow-elevation-sm">
      <div className="overflow-hidden rounded-lg border border-border bg-background shadow-sm">
        <div className="flex items-center gap-3 border-b border-border bg-muted/70 px-3 py-2">
          <div className="flex items-center gap-1.5" aria-hidden="true">
            <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
          </div>
          <p className="min-w-0 flex-1 truncate rounded-md bg-background/80 px-3 py-1 text-center text-[11px] font-medium text-muted-foreground">
            {t('demo.previewLabel')}
          </p>
          <span className="w-[42px]" aria-hidden="true" />
        </div>

        <div
          className="relative overflow-hidden"
          style={{ height: frameHeight || undefined }}
        >
          <div
            ref={innerRef}
            className="pointer-events-none origin-top-left select-none p-4 md:p-6"
            aria-hidden="true"
            style={{
              width: `${100 / PREVIEW_SCALE}%`,
              transform: `scale(${PREVIEW_SCALE})`,
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardPreview = () => {
  const { t, i18n } = useTranslation();
  const activities = useMemo(() => getDemoActivities(), [i18n.language]);

  const metrics = [
    {
      id: 1,
      title: t('dashboard.candidatesUpdated'),
      value: '12',
      icon: 'Users',
      trend: t('dashboard.totalCandidates', { count: 128 }),
      isNew: true,
    },
    {
      id: 2,
      title: t('dashboard.newAlerts'),
      value: '6',
      icon: 'Bell',
      trend: t('dashboard.openToWorkAlerts', { count: 2 }),
      isNew: true,
    },
    {
      id: 3,
      title: t('dashboard.identifiedSeekers'),
      value: '14',
      secondaryValue: 9,
      secondaryLabel: t('dashboard.potentials'),
      icon: 'Target',
      trend: t('dashboard.totalCandidates', { count: 128 }),
    },
  ];

  const usageMetrics = [
    {
      id: 1,
      title: t('dashboard.uploadLimit'),
      current: 128,
      total: 1000,
      unit: 'candidates',
      color: 'primary',
      footnote: t('dashboard.remaining', { count: 872 }),
    },
    {
      id: 2,
      title: t('dashboard.monthlyMinutes'),
      current: 47,
      total: 300,
      unit: 'minutes',
      color: 'warning',
      footnote: null,
    },
  ];

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
          {t('dashboard.title')}
        </h1>
        <p className="text-sm md:text-base text-muted-foreground">
          {t('dashboard.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {metrics.map((metric) => (
          <MetricCard
            key={metric.id}
            title={metric.title}
            value={metric.value}
            secondaryValue={metric.secondaryValue}
            secondaryLabel={metric.secondaryLabel}
            icon={metric.icon}
            trend={metric.trend}
            isNew={metric.isNew}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {usageMetrics.map((usage) => (
          <UsageMetric
            key={usage.id}
            title={usage.title}
            current={usage.current}
            total={usage.total}
            unit={usage.unit}
            color={usage.color}
            footnote={usage.footnote}
          />
        ))}
      </div>

      <RecentActivityTable activities={activities} loading={false} />
    </div>
  );
};

const AlertsPreview = () => {
  const { t, i18n } = useTranslation();
  const activities = useMemo(() => getDemoActivities(), [i18n.language]);

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
          {t('alerts.title')}
        </h1>
        <p className="text-sm md:text-base text-muted-foreground">
          {t('alerts.subtitle')}
        </p>
      </div>

      <AlertsSearchFilters
        candidateSearch=""
        onCandidateSearchChange={noop}
        dateFilter=""
        onDateFilterChange={noop}
        alertType="all"
        onAlertTypeChange={noop}
        totalAlerts={activities.length}
      />

      <AlertsTable activities={activities} />
    </div>
  );
};

const CandidatesPreview = () => {
  const { t } = useTranslation();
  const candidates = useMemo(() => getDemoCandidates(), []);

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
            {t('candidates.title')}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            {t('candidates.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <AddCandidateButton
            onClick={noop}
            isLimitReached={false}
            maxCandidates={1000}
            isTopTierPlan
          />
        </div>
      </div>

      <SearchAndFilters
        searchInput=""
        onSearchInputChange={noop}
        onSearchSubmit={noop}
        statusFilter="all"
        onStatusFilterChange={noop}
        contactFilter="all"
        onContactFilterChange={noop}
        totalCandidates={candidates.length}
        canPrioritize
      />

      <CandidatesTable
        candidates={candidates}
        onTogglePriority={noop}
        onDeleteCandidate={noop}
        userPlan="pro-and-voice"
        isRestricted={false}
        selectedIds={new Set()}
        onToggleSelect={noop}
        onToggleSelectAll={noop}
        totalFilteredCount={candidates.length}
      />
    </div>
  );
};

const ImportPreview = () => {
  const { t } = useTranslation();

  return (
    <div className="container mx-auto max-w-5xl">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">{t('import.title')}</h1>
          <p className="text-muted-foreground">{t('import.subtitle')}</p>
          <p className="text-sm text-muted-foreground mt-2">
            {t('import.slotsRemaining', {
              remaining: 872,
              current: 128,
              max: 1000,
            })}
          </p>
        </div>
        <UploadInstructions />
      </div>

      <div className="space-y-6">
        <FileUploadZone
          onFileSelect={noop}
          selectedFile={null}
          disabled={false}
          maxCandidates={1000}
          currentCount={128}
          remainingSlots={872}
        />
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" iconName="ArrowLeft" size="lg">
            {t('common.backToCandidates')}
          </Button>
          <Button iconName="Upload" size="lg">
            {t('import.uploadFile')}
          </Button>
        </div>
      </div>
    </div>
  );
};

const AtsPreview = () => {
  const { t } = useTranslation();

  return (
    <div className="container mx-auto max-w-5xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground mb-2">{t('connectAts.title')}</h1>
        <p className="text-muted-foreground">{t('connectAts.subtitle')}</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-success/10">
            <Icon name="CheckCircle2" size={24} className="text-success" />
          </div>
          <div>
            <p className="text-base font-semibold text-foreground">Zoho Recruit</p>
            <p className="text-sm text-success">{t('connectAts.connected')}</p>
          </div>
        </div>

        <p className="text-sm text-muted-foreground mb-6">
          {t('connectAts.authHint')}
        </p>

        <div className="flex flex-wrap gap-3">
          <Button variant="outline" className="inline-flex items-center gap-2">
            <Icon name="ArrowLeft" size={18} />
            {t('connectAts.backToDashboard')}
          </Button>
          <Button className="inline-flex items-center gap-2">
            <Icon name="Link" size={18} />
            {t('connectAts.reconnectZoho')}
          </Button>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-base font-semibold text-foreground mb-1">{t('connectAts.syncTitle')}</p>
            <p className="text-sm text-muted-foreground">
              {t('connectAts.syncDescription')}
            </p>
            <p className="mt-2 text-sm text-warning">
              {t('connectAts.syncWarning')}
            </p>
          </div>
          <Button type="button" className="inline-flex items-center gap-2 flex-shrink-0">
            <Icon name="RefreshCw" size={18} />
            {t('connectAts.syncButton')}
          </Button>
        </div>

        <div className="mt-5 rounded-lg border border-border bg-muted/30 px-4 py-3 space-y-3">
          <ul className="text-sm text-foreground space-y-1 list-none">
            <li>
              {t('connectAts.foundInZoho')}:{' '}
              <span className="font-semibold tabular-nums">64</span>
            </li>
            <li>
              {t('connectAts.correct')}:{' '}
              <span className="font-semibold tabular-nums">51</span>
            </li>
            <li>
              {t('connectAts.willBeImported')}:{' '}
              <span className="font-semibold tabular-nums">37</span>
            </li>
          </ul>
        </div>

        <div className="mt-5 rounded-lg border border-border bg-muted/30 px-4 py-3">
          <p className="text-sm font-semibold text-foreground mb-2">{t('connectAts.importFinished')}</p>
          <ul className="text-sm space-y-1 list-none">
            <li className="text-foreground">
              {t('connectAts.total')}:{' '}
              <span className="font-semibold tabular-nums">37</span>
            </li>
            <li className="text-success">
              {t('connectAts.added')}:{' '}
              <span className="font-semibold tabular-nums">25</span>
            </li>
            <li className="text-blue-600">
              {t('connectAts.updated')}:{' '}
              <span className="font-semibold tabular-nums">12</span>
            </li>
            <li className="text-warning">
              {t('connectAts.skipped')}:{' '}
              <span className="font-semibold tabular-nums">0</span>
            </li>
            <li className="text-destructive">
              {t('connectAts.failed')}:{' '}
              <span className="font-semibold tabular-nums">0</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const AiPreview = () => {
  const { t } = useTranslation();
  const calls = useMemo(() => getDemoCalls(), []);
  const activityPeriod = CALL_ACTIVITY_PERIODS.TODAY;

  const periodOptions = useMemo(
    () => CALL_ACTIVITY_PERIOD_OPTIONS.map((option) => ({
      ...option,
      label: t(`ai.overview.periods.${option.value}`),
    })),
    [t],
  );

  const metrics = [
    { id: 1, title: t('ai.overview.callsMade'), value: '18', icon: 'Phone' },
    { id: 2, title: t('ai.overview.noResponse'), value: '5', icon: 'PhoneOff' },
    { id: 3, title: t('ai.overview.openToMeet'), value: '4', icon: 'Handshake' },
    { id: 4, title: t('ai.overview.scheduledInterviews'), value: '3', icon: 'Calendar' },
  ];

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
          {t('ai.title')}
        </h1>
        <p className="text-sm md:text-base text-muted-foreground">
          {t('ai.subtitle')}
        </p>
      </div>

      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 md:mb-6">
          <h2 className="text-lg md:text-xl font-semibold text-foreground">
            {t('ai.overview.callActivity')}
          </h2>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
              {t('ai.overview.showing')}
            </span>
            <div className="w-full sm:w-56">
              <Select
                options={periodOptions}
                value={activityPeriod}
                onChange={noop}
                placeholder={t('ai.overview.selectPeriod')}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {metrics.map((metric) => (
            <MetricCard
              key={metric.id}
              title={metric.title}
              value={metric.value}
              icon={metric.icon}
            />
          ))}
        </div>
      </div>

      <UsageMetric
        title={t('ai.overview.monthlyMinutes')}
        current={47}
        total={300}
        unit="minutes"
        color="warning"
      />

      <AICallsTable calls={calls} showSectionHeader />
    </div>
  );
};

const PREVIEWS = {
  dashboard: DashboardPreview,
  alerts: AlertsPreview,
  candidates: CandidatesPreview,
  'import-candidates': ImportPreview,
  'connect-ats': AtsPreview,
  'ai-assistant': AiPreview,
};

const DemoStepPreview = ({ stepId }) => {
  const Preview = PREVIEWS[stepId];
  if (!Preview) return null;

  return (
    <PreviewFrame key={stepId}>
      <Preview />
    </PreviewFrame>
  );
};

export default DemoStepPreview;
